import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";
import {
  type ExamImage,
  type ExamType,
  type ExamWorksheetData,
  US_EXAM_TYPES,
} from "./epocus-exam-worksheets";

export const RXDB_EPOCUS_EXAM_COLLECTION_NAME = "epocus-exam";

export const NON_BILLABLE_REASONS = [
  "EDUCATIONAL",
  "NOT_MEDICALLY_INDICATED",
] as const;
export type NonBillableReason = (typeof NON_BILLABLE_REASONS)[number];

/**
 * An amendment or a confirmation of a signed report, appended by the clinician
 * who signed it. An amendment keeps the worksheet it replaced, so each earlier
 * version of the report stays viewable.
 */
export type ExamAmendment = {
  id: string;
  kind: "AMEND" | "CONFIRM";
  reason: string;
  signedAt: string;
  signedByUserId: string;
  reviewId: string | null;
  previousWorksheet: ExamWorksheetData | null;
};

export const useRxCollectionEpocusExam = (): RxCollection<EpocusExam> | null =>
  useRxCollection<EpocusExam>(RXDB_EPOCUS_EXAM_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<EpocusExam>>;
export type EpocusExamQueryConstructor = UseRxData["1"];

const EPOCUS_EXAM_SCHEMA_LITERAL = {
  title: "epocus exam schema",
  version: 8,
  description:
    "Represents a protocol-specific POCUS assessment within an encounter",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    encounterId: {
      type: "string",
      maxLength: 100,
    },
    examType: {
      type: "string",
      enum: [
        "efast",
        "cardiac",
        "lung",
        "bladder",
        "renal",
        "aorta",
        "pregnancy",
        "msk",
        "guided_joint_injection",
        "guided_tendon_injection",
        "guided_nerve_block",
        ...US_EXAM_TYPES,
      ],
    },
    // biome-ignore lint/suspicious/noExplicitAny: ExamWorksheetData is a discriminated union; JSON Schema cannot express this. Type override below.
    worksheetData: { type: "object", additionalProperties: true } as any,
    // biome-ignore lint/suspicious/noExplicitAny: ExamImage[] — shape enforced by TypeScript
    images: { type: "array", items: { type: "object" } } as any,
    status: {
      type: "string",
      enum: ["draft", "submitted"],
    },
    signedAt: { type: ["string", "null"], format: "date-time" },
    signedByUserId: { type: ["string", "null"], maxLength: 100 },
    nonBillable: { type: "boolean" },
    nonBillableReason: {
      type: ["string", "null"],
      enum: [...NON_BILLABLE_REASONS, null],
    },
    // biome-ignore lint/suspicious/noExplicitAny: ExamAmendment[] — shape enforced by TypeScript
    amendments: { type: "array", items: { type: "object" } } as any,
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "encounterId",
    "examType",
    "worksheetData",
    "images",
    "status",
    "signedAt",
    "signedByUserId",
    "nonBillable",
    "nonBillableReason",
    "amendments",
    "meta",
  ],
  indexes: ["encounterId"],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_EXAM_SCHEMA_LITERAL);

type RawEpocusExam = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export type EpocusExam = Omit<
  RawEpocusExam,
  "examType" | "worksheetData" | "images" | "nonBillableReason" | "amendments"
> & {
  nonBillableReason: NonBillableReason | null;
  amendments: ExamAmendment[];
  examType: ExamType;
  worksheetData: ExamWorksheetData;
  images: ExamImage[];
};

export const EPOCUS_EXAM_SCHEMA: RxJsonSchema<EpocusExam> =
  EPOCUS_EXAM_SCHEMA_LITERAL as unknown as RxJsonSchema<EpocusExam>;
